// Web speech-to-text provider for the SDK's VoiceMealFlow.
//
// The SDK ships no STT implementation on purpose — VoiceMealFlow reads from
// a <VoiceCaptureProvider> and the host app supplies a hook conforming to
// `VoiceCaptureHook` ({ status, transcript, start, stop, reset }). On native
// that hook wraps expo-speech-recognition; here we wrap the browser's
// built-in Web Speech API, which does on-device speech-to-text and hands
// back a transcript directly — no audio-file plumbing.
//
// Browser support: Chrome / Edge / Safari. Firefox lacks the API, so the
// hook reports status='unsupported' and the SDK UI degrades gracefully.

import { useCallback, useRef, useState } from 'react';

type VoiceCaptureStatus =
  | 'idle'
  | 'starting'
  | 'recording'
  | 'stopping'
  | 'error'
  | 'unsupported';

interface VoiceCaptureHook {
  status: VoiceCaptureStatus;
  transcript: string;
  error?: string;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  reset: () => void;
}

// `SpeechRecognition` is unprefixed in the spec but only shipped behind the
// `webkit` prefix in Chrome/Safari. Not in the lib.dom types, hence `any`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SpeechRecognitionImpl: any =
  typeof window !== 'undefined'
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    : undefined;

export function useWebVoiceCapture(): VoiceCaptureHook {
  const [status, setStatus] = useState<VoiceCaptureStatus>(
    SpeechRecognitionImpl ? 'idle' : 'unsupported',
  );
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | undefined>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recRef = useRef<any>(null);

  const start = useCallback(async () => {
    if (!SpeechRecognitionImpl) {
      setStatus('unsupported');
      return;
    }
    setStatus('starting');
    setTranscript('');
    setError(undefined);
    try {
      const rec = new SpeechRecognitionImpl();
      rec.lang = 'en-US';
      rec.continuous = true;
      rec.interimResults = true;
      rec.onstart = () => setStatus('recording');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rec.onresult = (ev: any) => {
        let text = '';
        for (let i = 0; i < ev.results.length; i += 1) {
          text += ev.results[i][0].transcript;
        }
        setTranscript(text.trim());
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rec.onerror = (ev: any) => {
        setError(
          typeof ev?.error === 'string' ? ev.error : 'speech recognition error',
        );
        setStatus('error');
      };
      // `onend` fires after stop() and also when the engine stops on its own;
      // settle back to idle unless an error already moved us elsewhere.
      rec.onend = () =>
        setStatus((s) => (s === 'recording' || s === 'stopping' ? 'idle' : s));
      recRef.current = rec;
      rec.start();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStatus('error');
    }
  }, []);

  const stop = useCallback(async () => {
    setStatus('stopping');
    try {
      recRef.current?.stop();
    } catch {
      /* recognition already stopped — no-op */
    }
  }, []);

  const reset = useCallback(() => {
    try {
      recRef.current?.abort();
    } catch {
      /* no-op */
    }
    recRef.current = null;
    setTranscript('');
    setError(undefined);
    setStatus(SpeechRecognitionImpl ? 'idle' : 'unsupported');
  }, []);

  return { status, transcript, error, start, stop, reset };
}
